// 몹·보스 GLB 로더: 정적 대체판 또는 GPU 스키닝용 골격 텍스처·LOD를 준비한다.
// 실패하면 호출한 쪽이 절차 그레이박스 지오메트리로 대신한다(게임은 계속 플레이된다).
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { bakeSkin, type SkinAtlas } from './skinning';
import { appendAttachments, type AttachSet } from './regionAttach';
import { resolveAssetUrl } from '../../pwa/assetMap';

export interface ActorAsset {
  /** position·normal·uv(+aSmooth, aPaint) 속성 지오메트리(발이 원점) */
  geometry: THREE.BufferGeometry;
  map: THREE.Texture | null;
  /** aPaint(텍스처를 정점에 평균 낸 매끈한 칠 색) 속성이 있는지 */
  paint: boolean;
  /** 모델 높이(m)와 XZ 반폭(m) */
  height: number;
  halfWidth: number;
  skin?: SkinAtlas;
  lod?: ActorAsset;
}

const base = (): string => import.meta.env.BASE_URL ?? './';

/**
 * 클립이 든 단일 GLB로 내려받는 액터(이름 그대로 읽고, 굽는 시점에 바닥 관통을 보정한다).
 * 나머지(R1 몹·보스)는 assets/r1/<이름>_anim.glb 골격판과 <이름>.glb 정적판을 쓴다.
 */
const DIRECT_ACTORS: Record<string, string> = {
  mob_mushroom_shaman: 'assets/monsters/',
  // R2 잿불 협곡 전용(20261018-r2-enemies). 클립은 R1 몹·버섯 주술사 클립을 같은 길이로 옮긴 것이다.
  mob_ember_goblin: 'assets/monsters/',
  mob_obsidian_shield: 'assets/monsters/',
  mob_magma_boar: 'assets/monsters/',
  boss_ember_archmage: 'assets/bosses/',
};

export const isDirectActor = (name: string): boolean => name in DIRECT_ACTORS;

export function actorUrl(name: string): string {
  return resolveAssetUrl(base() + (DIRECT_ACTORS[name] ?? 'assets/r1/') + name + '.glb');
}

/**
 * 'aSmooth' 속성: 위치가 같은(1e-4m 이내) 정점을 하나로 묶고, 면적 가중 면 법선을 모은 뒤
 * 이웃 삼각형끼리 iterations번 평균 낸 매끈한 법선. 로드 때 한 번만 돈다.
 * Tripo 감면 메시는 면이 구겨져 있어(면·정점 법선 차 중앙값 약 40°) 툰 경계와 반전 헐이 조각나 보인다.
 * 조명은 원래 법선과 섞어 쓰고, 외곽선은 이것만으로 부풀린다(UV 이음매에서도 갈라지지 않는다).
 */
export function addSmoothNormals(geometry: THREE.BufferGeometry, iterations = 2): void {
  const pos = geometry.getAttribute('position');
  const n = pos.count;
  const out = new Float32Array(n * 3);
  const { group, G } = groupByPosition(pos);
  let acc = new Float32Array(G * 3);
  const index = geometry.getIndex();
  const triCount = index ? index.count / 3 : n / 3;
  const tri = new Int32Array(triCount * 3);
  for (let t = 0; t < triCount * 3; t++) tri[t] = group[index ? index.getX(t) : t];
  // 면적 가중 면 법선(외적 길이 = 면적 × 2)
  for (let t = 0; t < triCount; t++) {
    const a = index ? index.getX(t * 3) : t * 3, b = index ? index.getX(t * 3 + 1) : t * 3 + 1, c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    const ux = pos.getX(b) - pos.getX(a), uy = pos.getY(b) - pos.getY(a), uz = pos.getZ(b) - pos.getZ(a);
    const vx = pos.getX(c) - pos.getX(a), vy = pos.getY(c) - pos.getY(a), vz = pos.getZ(c) - pos.getZ(a);
    const fx = uy * vz - uz * vy, fy = uz * vx - ux * vz, fz = ux * vy - uy * vx;
    for (let k = 0; k < 3; k++) { const gi = tri[t * 3 + k] * 3; acc[gi] += fx; acc[gi + 1] += fy; acc[gi + 2] += fz; }
  }
  const norm = (arr: Float32Array): void => {
    for (let gi = 0; gi < G; gi++) {
      const o = gi * 3, l = Math.hypot(arr[o], arr[o + 1], arr[o + 2]) || 1;
      arr[o] /= l; arr[o + 1] /= l; arr[o + 2] /= l;
    }
  };
  norm(acc);
  // 이웃 평균: 삼각형 세 꼭짓점 법선의 합을 각 꼭짓점에 더한다
  for (let it = 0; it < iterations; it++) {
    const next = acc.slice();
    for (let t = 0; t < triCount; t++) {
      const a = tri[t * 3] * 3, b = tri[t * 3 + 1] * 3, c = tri[t * 3 + 2] * 3;
      for (let d = 0; d < 3; d++) {
        const s = acc[a + d] + acc[b + d] + acc[c + d];
        next[a + d] += s; next[b + d] += s; next[c + d] += s;
      }
    }
    norm(next);
    acc = next;
  }
  for (let i = 0; i < n; i++) {
    const gi = group[i] * 3;
    out[i * 3] = acc[gi]; out[i * 3 + 1] = acc[gi + 1]; out[i * 3 + 2] = acc[gi + 2];
  }
  geometry.setAttribute('aSmooth', new THREE.BufferAttribute(out, 3));
}

/** 텍스처 이미지 픽셀을 읽는다(로드 때 한 번). 읽을 수 없으면 null. */
function readPixels(map: THREE.Texture): { data: Uint8ClampedArray; w: number; h: number } | null {
  const img = map.image as (CanvasImageSource & { width: number; height: number }) | undefined;
  if (!img || !img.width || !img.height || typeof document === 'undefined') return null;
  // 평균 색만 쓰므로 256px로 줄여 읽는다(텍스처 조각 크기보다 충분히 촘촘하다)
  const w = Math.min(256, img.width), h = Math.min(256, img.height);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h };
}

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/**
 * 'aPaint' 속성: 삼각형마다 텍스처 여러 점의 평균 색을 면적 가중으로 정점(같은 위치 묶음)에 모은 뒤
 * 이웃끼리 iterations번 평균 낸 선형 색. 로드 때 한 번만 돈다.
 * Tripo 텍스처는 작은 UV 조각 모자이크라, 감면된 큰 삼각형이 조각마다 다른 색을 읽어 면 단위로 각져 보인다.
 * 셰이더는 이 매끈한 칠 색에 원래 텍스처를 일부 섞어 세부를 남긴다(콘셉트의 큰 색면, ART-BIBLE 5장).
 */
export function addPaintColors(geometry: THREE.BufferGeometry, map: THREE.Texture, iterations = 2): boolean {
  const px = readPixels(map);
  const uv = geometry.getAttribute('uv');
  if (!px || !uv) return false;
  const pos = geometry.getAttribute('position');
  const n = pos.count;
  const { group, G } = groupByPosition(pos);
  const index = geometry.getIndex();
  const triCount = index ? index.count / 3 : n / 3;
  const acc = new Float32Array(G * 3);
  const wsum = new Float32Array(G);
  // 삼각형 내부 표본점(무게중심 좌표)
  const B = [[1 / 3, 1 / 3, 1 / 3], [0.6, 0.2, 0.2], [0.2, 0.6, 0.2], [0.2, 0.2, 0.6], [0.45, 0.45, 0.1], [0.1, 0.45, 0.45], [0.45, 0.1, 0.45]];
  const sample = (u: number, v: number, o: Float32Array): void => {
    // glTF UV는 왼쪽 위가 원점이고 GLTFLoader는 flipY=false라 그대로 픽셀 좌표가 된다
    const x = Math.min(px.w - 1, Math.max(0, Math.floor((u - Math.floor(u)) * px.w)));
    const y = Math.min(px.h - 1, Math.max(0, Math.floor((v - Math.floor(v)) * px.h)));
    const k = (y * px.w + x) * 4;
    o[0] += srgbToLinear(px.data[k] / 255); o[1] += srgbToLinear(px.data[k + 1] / 255); o[2] += srgbToLinear(px.data[k + 2] / 255);
  };
  const col = new Float32Array(3);
  for (let t = 0; t < triCount; t++) {
    const a = index ? index.getX(t * 3) : t * 3, b = index ? index.getX(t * 3 + 1) : t * 3 + 1, c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    col[0] = col[1] = col[2] = 0;
    for (const w of B) sample(uv.getX(a) * w[0] + uv.getX(b) * w[1] + uv.getX(c) * w[2], uv.getY(a) * w[0] + uv.getY(b) * w[1] + uv.getY(c) * w[2], col);
    const ux = pos.getX(b) - pos.getX(a), uy = pos.getY(b) - pos.getY(a), uz = pos.getZ(b) - pos.getZ(a);
    const vx = pos.getX(c) - pos.getX(a), vy = pos.getY(c) - pos.getY(a), vz = pos.getZ(c) - pos.getZ(a);
    const area = Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) + 1e-9;
    const s = area / B.length;
    for (const vi of [a, b, c]) {
      const gi = group[vi];
      acc[gi * 3] += col[0] * s; acc[gi * 3 + 1] += col[1] * s; acc[gi * 3 + 2] += col[2] * s; wsum[gi] += area;
    }
  }
  let cur = new Float32Array(G * 3);
  for (let gi = 0; gi < G; gi++) {
    const w = wsum[gi] || 1;
    cur[gi * 3] = acc[gi * 3] / w; cur[gi * 3 + 1] = acc[gi * 3 + 1] / w; cur[gi * 3 + 2] = acc[gi * 3 + 2] / w;
  }
  // 이웃 평균(자기 자신 포함 삼각형 단위 라플라시안)
  for (let it = 0; it < iterations; it++) {
    const next = new Float32Array(G * 3);
    const cnt = new Float32Array(G);
    for (let t = 0; t < triCount; t++) {
      const ga = group[index ? index.getX(t * 3) : t * 3], gb = group[index ? index.getX(t * 3 + 1) : t * 3 + 1], gc = group[index ? index.getX(t * 3 + 2) : t * 3 + 2];
      for (let d = 0; d < 3; d++) {
        const sum = cur[ga * 3 + d] + cur[gb * 3 + d] + cur[gc * 3 + d];
        next[ga * 3 + d] += sum; next[gb * 3 + d] += sum; next[gc * 3 + d] += sum;
      }
      cnt[ga] += 3; cnt[gb] += 3; cnt[gc] += 3;
    }
    for (let gi = 0; gi < G; gi++) {
      const k = cnt[gi] || 1;
      for (let d = 0; d < 3; d++) next[gi * 3 + d] = cnt[gi] ? next[gi * 3 + d] / k : cur[gi * 3 + d];
    }
    cur = next;
  }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const gi = group[i] * 3;
    out[i * 3] = cur[gi]; out[i * 3 + 1] = cur[gi + 1]; out[i * 3 + 2] = cur[gi + 2];
  }
  geometry.setAttribute('aPaint', new THREE.BufferAttribute(out, 3));
  return true;
}

/** 위치가 같은(1e-4m 이내) 정점을 한 묶음 번호로 모은다. */
function groupByPosition(pos: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): { group: Int32Array; G: number } {
  const n = pos.count;
  const key = new Map<string, number>();
  const group = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const k = Math.round(pos.getX(i) * 1e4) + ',' + Math.round(pos.getY(i) * 1e4) + ',' + Math.round(pos.getZ(i) * 1e4);
    let gi = key.get(k);
    if (gi === undefined) { gi = key.size; key.set(k, gi); }
    group[i] = gi;
  }
  return { group, G: key.size };
}

/** GLB 하나를 읽는다. 실패하면 null. optional이면 아직 없는 에셋으로 보고 정보 로그만 남긴다. */
export async function loadActor(name: string, optional = false): Promise<ActorAsset | null> {
  try {
    const gltf = await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(actorUrl(name));
    let mesh: THREE.Mesh | null = null;
    gltf.scene.traverse((o) => { if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh; });
    if (!mesh) throw new Error('메시가 없다');
    const src = mesh as THREE.Mesh;
    src.updateWorldMatrix(true, false);
    const geometry = src.geometry.clone();
    geometry.applyMatrix4(src.matrixWorld);
    for (const key of Object.keys(geometry.attributes)) {
      if (key !== 'position' && key !== 'normal' && key !== 'uv') geometry.deleteAttribute(key);
    }
    addSmoothNormals(geometry);
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const mat = src.material as THREE.MeshStandardMaterial;
    const map = mat.map ?? null;
    if (map) { map.anisotropy = 4; map.needsUpdate = true; }
    let paint = false;
    try { paint = map ? addPaintColors(geometry, map) : false; } catch (err) { console.warn('칠 색 계산 실패(텍스처만 쓴다): ' + name, err); }
    return { geometry, map, paint, height: bb.max.y - bb.min.y, halfWidth: Math.max(bb.max.x, -bb.min.x) };
  } catch (err) {
    if (optional) console.info('선택 에셋 없음(대체 모델 사용): ' + name);
    else console.warn('액터 GLB를 불러오지 못해 그레이박스로 대신한다: ' + name, err);
    return null;
  }
}

/**
 * 애니메이션판이 불완전하면 전부 버리고 기존 정적판으로 되돌린다.
 * attach(c021 지역 변형 조각)를 주면 원본·저폴리 메시 뒤에 조각을 본 가중치 1로 이어 붙이고 aAtt 속성을 단다(정적판 대체에는 붙지 않는다 — skin으로 확인).
 */
export async function loadAnimatedActor(name: string, required: readonly string[], attach?: AttachSet | null): Promise<ActorAsset | null> {
  const owned: ActorAsset[] = [];
  try {
    const direct = isDirectActor(name);
    const gltf = await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(actorUrl(direct ? name : name + '_anim'));
    for (const key of required) if (!gltf.animations.some(c => c.name === key)) throw new Error('필수 클립 없음: ' + key);
    const meshes: THREE.SkinnedMesh[] = [];
    gltf.scene.traverse(o => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) meshes.push(o as THREE.SkinnedMesh); });
    const main = meshes.find(m => m.name === 'body') ?? (direct ? meshes[0] : undefined);
    if (!main) throw new Error('body 스킨 메시 없음');
    const prepare = (mesh: THREE.SkinnedMesh): ActorAsset => {
      gltf.scene.updateMatrixWorld(true);
      const geometry = mesh.geometry.clone();
      // 정규화 정수 속성에 월드 좌표를 다시 쓰면 ±1 밖이 잘린다. 변형 전에 실수로 펼친다.
      for (const key of ['position', 'normal']) {
        const source = geometry.getAttribute(key);
        const values = new Float32Array(source.count * 3);
        for (let i = 0; i < source.count; i++) { values[i * 3] = source.getX(i); values[i * 3 + 1] = source.getY(i); values[i * 3 + 2] = source.getZ(i); }
        geometry.setAttribute(key, new THREE.BufferAttribute(values, 3));
      }
      geometry.applyMatrix4(mesh.matrixWorld);
      if (!geometry.hasAttribute('skinIndex') || !geometry.hasAttribute('skinWeight')) throw new Error('스킨 속성 없음');
      addSmoothNormals(geometry);
      geometry.computeBoundingBox();
      // meshopt 양자화 해제 변환은 inverseBindMatrices 안에도 들어간다. 원시 AABB 높이는 모델 키가 아니다.
      mesh.skeleton.update(); mesh.computeBoundingBox();
      const bb = mesh.boundingBox!.clone().applyMatrix4(mesh.matrixWorld);
      const rest = new Float32Array(geometry.getAttribute('position').count * 3), point = new THREE.Vector3();
      for (let i = 0; i < rest.length / 3; i++) {
        point.fromBufferAttribute(mesh.geometry.getAttribute('position'), i);
        mesh.applyBoneTransform(i, point).applyMatrix4(mesh.matrixWorld).toArray(rest, i * 3);
      }
      geometry.setAttribute('aActorRest', new THREE.BufferAttribute(rest, 3));
      const material = mesh.material as THREE.MeshStandardMaterial;
      const map = material.map ?? null;
      if (map) { map.anisotropy = 4; map.needsUpdate = true; }
      const asset: ActorAsset = { geometry, map, paint: false, height: bb.max.y - bb.min.y, halfWidth: Math.max(bb.max.x, -bb.min.x) };
      owned.push(asset);
      try { asset.paint = map ? addPaintColors(geometry, map) : false; } catch { /* 텍스처만 사용 */ }
      if (attach) {
        asset.geometry = appendAttachments(geometry, mesh, mesh === main ? attach.hi : attach.lod, name);
        geometry.dispose();
      }
      interleaveActorAttributes(asset.geometry);
      asset.skin = bakeSkin(gltf.scene, mesh, gltf.animations, direct);
      return asset;
    };
    const asset = prepare(main);
    const low = meshes.find(m => m.name === 'body_lod1');
    if (low) { asset.lod = prepare(low); asset.lod.height = asset.height; }
    return asset;
  } catch (error) {
    for (const asset of owned) { asset.geometry.dispose(); asset.skin?.texture.dispose(); }
    console.warn('골격 에셋을 읽지 못해 정적판으로 대신한다: ' + name, error);
    return loadActor(name, true);
  }
}

/** WebGPU 최소 보장 8개 정점 버퍼 안에 들어오게 모든 정적 속성을 한 버퍼로 묶는다. */
export function interleaveActorAttributes(geometry: THREE.BufferGeometry): void {
  const names = Object.keys(geometry.attributes);
  const count = geometry.getAttribute('position').count;
  const stride = names.reduce((sum, name) => sum + geometry.getAttribute(name).itemSize, 0);
  const data = new Float32Array(count * stride);
  const buffer = new THREE.InterleavedBuffer(data, stride);
  let offset = 0;
  for (const name of names) {
    const source = geometry.getAttribute(name);
    for (let i = 0; i < count; i++) for (let c = 0; c < source.itemSize; c++) data[i * stride + offset + c] = source.getComponent(i, c);
    geometry.setAttribute(name, new THREE.InterleavedBufferAttribute(buffer, source.itemSize, offset));
    offset += source.itemSize;
  }
}
